import { flagImages } from '@elements/Flag';
import {
  ArrowsHorizontal,
  Check,
  Equals,
  NotEqualsIcon,
  X,
} from '@phosphor-icons/react';
import {
  Button,
  cn,
  DatePicker,
  Popover,
  SearchField,
  Select,
  Spinner,
  TextField,
} from '@skupreme/shared/ui';
import { DateFormat, DateUtility } from '@utility/date';
import {
  buildQuery,
  clearedValue,
  clearedValues,
  countApplied,
  filterTypes,
  hasAppliedValue,
  initialValues,
  isOptionChosen,
  pickOption,
  searchOptions,
  sortOptionsByText,
  type FilterConfig,
  type FilterOption,
  type FilterState,
  type FilterValue,
  type FilterValues,
  fromDay,
  toDay,
} from '@utility/list-filter';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

export { filterTypes };

const filterOptions = [
  {
    value: filterTypes.is,
    text: 'Equals',
    conditionImage: <Equals size={8} weight="bold" />,
  },
  {
    value: filterTypes.isNot,
    text: 'Does Not Equal',
    conditionImage: <NotEqualsIcon height={8} />,
  },
];

function withIcon(icon: ReactNode, size: number) {
  return isValidElement(icon)
    ? cloneElement(icon as ReactElement<{ size?: number }>, { size })
    : null;
}

function Range({ from, to }: { from: ReactNode; to: ReactNode }) {
  return (
    <span className="sk:inline-flex sk:items-center">
      {from}
      <ArrowsHorizontal className="sk:mx-1" size={10} />
      {to}
    </span>
  );
}

type FilterButtonProps = {
  filters?: FilterConfig[];
  loading?: boolean;
  /** Receives the query to fetch the list with. */
  setFinalFilter: (query: Record<string, unknown>) => void;
  setFinalvalue: (values: FilterValues) => void;
  /** The applied values; the caller owns them so FilterPill can read them. */
  finalValue: FilterValues;
  /** Which filter's editor is open; -1 for the list of filters. */
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  btnClassName?: string;
  /** The smaller trigger, for toolbars. */
  mini?: boolean;
};

/** "Filters" with a count, opening the list of filters and each one's editor. */
export function FilterButton({
  filters,
  loading = false,
  setFinalFilter,
  setFinalvalue,
  finalValue,
  activeIndex,
  setActiveIndex,
  open,
  setOpen,
  btnClassName = '',
  mini = false,
}: FilterButtonProps) {
  const [userValue, setUserValue] = useState<FilterValues>({});
  const [localFilter, setLocalFilter] = useState<string | null>(null);

  const applyFilter = () => {
    if (hasAppliedValue(userValue)) {
      setFinalvalue(userValue);
    }
  };

  useEffect(() => {
    try {
      setFinalFilter(localFilter ? JSON.parse(localFilter) : {});
    } catch {
      /* A malformed query keeps the last one. */
    }
  }, [localFilter]);

  useEffect(() => {
    if (Object.keys(finalValue).length < 1) return;
    setLocalFilter(JSON.stringify(buildQuery(finalValue)));
    setActiveIndex(-1);
    setOpen(false);
  }, [finalValue]);

  useEffect(() => {
    setUserValue(finalValue);
  }, [finalValue]);

  useEffect(() => {
    if (open) setUserValue(finalValue);
  }, [open]);

  useEffect(() => {
    const values = initialValues(filters);
    setUserValue(values);
    setFinalvalue(values);
  }, [filters]);

  const closeSelection = () => {
    setUserValue(finalValue);
    setActiveIndex(-1);
  };

  const closeDropdown = () => {
    closeSelection();
    setOpen(false);
  };

  const updateFilter = (name: string, value: FilterState) => {
    const data = { ...userValue, [name]: value };
    setUserValue(data);
    setFinalvalue(data);
  };

  const updateMultiple = (name: string, patch: Partial<FilterState>) => {
    setUserValue({ ...userValue, [name]: { ...userValue[name], ...patch } });
  };

  const clearAllFilters = () => {
    const values = clearedValues(filters, finalValue);
    setUserValue(values);
    setFinalvalue(values);
  };

  const numberOfFilters = useMemo(() => countApplied(finalValue), [finalValue]);
  const activeFilter = activeIndex >= 0 ? filters?.[activeIndex] : undefined;

  return (
    <div className="sk:inline-flex sk:items-center sk:gap-1">
      <Popover
        aria-label="Filters"
        placement="bottom end"
        isOpen={open}
        onOpenChange={isOpen => (isOpen ? setOpen(true) : closeDropdown())}
        className="sk:w-80 sk:max-w-[calc(100vw-2rem)]"
        trigger={
          <Button
            variant="pill"
            size={mini ? 'sm' : 'md'}
            className={cn(btnClassName === 'light-btn' ? '' : btnClassName)}
          >
            {loading ? <Spinner size="sm" label="Loading" /> : null}
            Filters
            {numberOfFilters ? (
              <span className="sk:text-fg-tertiary">({numberOfFilters})</span>
            ) : null}
          </Button>
        }
      >
        {activeFilter ? (
          <ActiveFilter
            key={activeFilter.name}
            item={activeFilter}
            index={activeIndex}
            filter={userValue[activeFilter.name] ?? {}}
            updateMultiple={patch => updateMultiple(activeFilter.name, patch)}
            clearFilter={() =>
              updateFilter(activeFilter.name, clearedValue(activeFilter))
            }
            applyFilter={applyFilter}
            close={closeSelection}
          />
        ) : (
          <FilterList
            filters={filters}
            userValue={userValue}
            onPick={setActiveIndex}
            onClearAll={clearAllFilters}
          />
        )}
      </Popover>
      {numberOfFilters ? (
        <Button
          variant="ghost"
          size="sm"
          shape="circle"
          aria-label="Clear all filters"
          onPress={clearAllFilters}
        >
          <X size={mini ? 12 : 14} aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}

function FilterList({
  filters,
  userValue,
  onPick,
  onClearAll,
}: {
  filters?: FilterConfig[];
  userValue: FilterValues;
  onPick: (index: number) => void;
  onClearAll: () => void;
}) {
  return (
    <div className="sk:flex sk:flex-col">
      <div className="sk:flex sk:items-center sk:justify-between sk:px-2 sk:py-2">
        <span className="sk:text-sm sk:font-semibold">Filter By</span>
        <Button variant="ghost" size="sm" onPress={onClearAll}>
          Clear Filters
        </Button>
      </div>
      {filters?.map((filter, index) =>
        filter.skip ? null : (
          <button
            key={filter.name}
            type="button"
            onClick={() => onPick(index)}
            className="sk:flex sk:w-full sk:cursor-pointer sk:items-center sk:justify-between sk:gap-2 sk:rounded-option sk:border-0 sk:border-t sk:border-solid sk:border-rule sk:bg-transparent sk:px-2 sk:py-2 sk:text-left sk:text-dense sk:text-fg sk:hover:bg-fill-quaternary"
          >
            <span className="sk:flex sk:items-center sk:gap-2">
              {withIcon(filter.icon, 20)}
              {filter.label}
            </span>
            <ChosenCount filter={filter} state={userValue[filter.name]} />
          </button>
        ),
      )}
    </div>
  );
}

function ChosenCount({
  filter,
  state,
}: {
  filter: FilterConfig;
  state?: FilterState;
}) {
  const count = filter.multiple
    ? ((state?.value as FilterValue[] | undefined)?.length ?? 0)
    : state?.value
      ? 1
      : 0;
  return count ? <span className="sk:text-fg-tertiary">{count}</span> : null;
}

type ActiveFilterProps = {
  item: FilterConfig;
  index: number;
  filter: FilterState;
  updateMultiple: (patch: Partial<FilterState>) => void;
  clearFilter: () => void;
  applyFilter: () => void;
  close: () => void;
};

function ActiveFilter({
  item,
  index,
  filter,
  updateMultiple,
  clearFilter,
  applyFilter,
  close,
}: ActiveFilterProps) {
  const isBetween = filter.type === 'BETWEEN';
  const values = (
    Array.isArray(filter.value) ? filter.value : []
  ) as FilterValue[];

  const setOption = (option: {
    value: FilterValue | FilterValue[];
    text?: ReactNode;
  }) => updateMultiple(pickOption(item, filter, option, index));

  const conditions = (item.custom_options ?? filterOptions).map(option => ({
    value: option.value,
    label: option.text ?? option.value,
  }));

  return (
    <div className="sk:flex sk:flex-col sk:gap-3 sk:p-2">
      <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
        <span className="sk:flex sk:items-center sk:gap-2">
          <span className="sk:text-fg-tertiary">{item.label}</span>
          <Select
            aria-label={`${typeof item.label === 'string' ? item.label : 'Filter'} condition`}
            options={conditions}
            value={filter.type}
            onChange={type =>
              updateMultiple({
                value: type === 'BETWEEN' ? ['', ''] : '',
                toShow: '',
                type,
                index,
              })
            }
          />
        </span>
        <Button variant="ghost" size="sm" onPress={clearFilter}>
          Clear Filter
        </Button>
      </div>
      <div className="sk:border-t sk:border-solid sk:border-rule" />

      {item.type === 'text' ? (
        <TextEditor
          item={item}
          filter={filter}
          isBetween={isBetween}
          values={values}
          setOption={setOption}
        />
      ) : item.type === 'date' ? (
        <DateEditor
          filter={filter}
          isBetween={isBetween}
          values={values}
          setOption={setOption}
        />
      ) : (
        <OptionsEditor item={item} filter={filter} setOption={setOption} />
      )}

      <div className="sk:flex sk:gap-2">
        <Button
          variant="primary"
          size="md"
          className="sk:flex-1"
          onPress={applyFilter}
        >
          Apply
        </Button>
        <Button variant="pill" size="md" className="sk:flex-1" onPress={close}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

type EditorProps = {
  filter: FilterState;
  setOption: (option: {
    value: FilterValue | FilterValue[];
    text?: ReactNode;
  }) => void;
};

function TextEditor({
  item,
  filter,
  isBetween,
  values,
  setOption,
}: EditorProps & {
  item: FilterConfig;
  isBetween: boolean;
  values: FilterValue[];
}) {
  const label = typeof item.label === 'string' ? item.label : 'value';
  const numeric = isBetween || !!item.numeric;
  const prefix = isValidElement(item.prefix) ? cloneElement(item.prefix) : null;

  if (isBetween) {
    const [from = '', to = ''] = values.map(value => String(value ?? ''));
    return (
      <div className="sk:flex sk:items-center sk:gap-2">
        {prefix}
        <TextField
          label={`${label} from`}
          placeholder="From"
          type="number"
          value={from}
          onChange={next =>
            setOption({
              value: [next, to],
              text: <Range from={next} to={to} />,
            })
          }
        />
        <span>-</span>
        <TextField
          label={`${label} to`}
          placeholder="To"
          type="number"
          value={to}
          onChange={next =>
            setOption({
              value: [from, next],
              text: <Range from={from} to={next} />,
            })
          }
        />
      </div>
    );
  }

  return (
    <div className="sk:flex sk:items-center sk:gap-2 sk:[&>*:last-child]:flex-1">
      {prefix}
      <TextField
        label={label}
        placeholder={item.placeholder || `Search for ${label}`}
        type={numeric ? 'number' : 'text'}
        value={String(filter.value ?? '')}
        onChange={next => setOption({ value: next, text: next })}
      />
    </div>
  );
}

function DateEditor({
  filter,
  isBetween,
  values,
  setOption,
}: EditorProps & { isBetween: boolean; values: FilterValue[] }) {
  if (isBetween) {
    const [start = null, end = null] = values;
    const update = (nextStart: FilterValue, nextEnd: FilterValue) =>
      setOption({
        value: [nextStart, nextEnd],
        text: (
          <Range
            from={DateUtility.formatDate(
              nextStart as Date,
              DateFormat.yearMonthDate,
            )}
            to={DateUtility.formatDate(
              nextEnd as Date,
              DateFormat.yearMonthDate,
            )}
          />
        ),
      });
    return (
      <div className="sk:flex sk:flex-col sk:gap-2">
        <DatePicker
          aria-label="Start"
          value={toDay(start)}
          onChange={day => update(fromDay(day), end)}
        />
        <DatePicker
          aria-label="End"
          value={toDay(end)}
          onChange={day => update(start, fromDay(day))}
        />
      </div>
    );
  }

  return (
    <DatePicker
      aria-label="Date"
      value={toDay(filter.value as FilterValue)}
      onChange={day => {
        const date = fromDay(day);
        setOption({
          value: date,
          text: DateUtility.formatDate(date, DateFormat.yearMonthDate),
        });
      }}
    />
  );
}

function OptionsEditor({
  item,
  filter,
  setOption,
}: EditorProps & { item: FilterConfig }) {
  const [search, setSearch] = useState('');
  const options = useMemo(() => {
    const matches = searchOptions(item, search);
    return item.sorted === false ? matches : sortOptionsByText(matches);
  }, [item, search]);

  return (
    <div className="sk:flex sk:flex-col sk:gap-2">
      {item.search || item.multiple ? (
        <SearchField
          aria-label={`Search ${typeof item.label === 'string' ? item.label : 'options'}`}
          placeholder="Search for one or more options..."
          value={search}
          onChange={setSearch}
          className="sk:w-full"
        />
      ) : null}
      {options.length > 0 ? (
        <div className="sk:flex sk:max-h-50 sk:flex-col sk:overflow-y-auto">
          {options.map((option: FilterOption) => (
            <OptionRow
              key={String(option.value)}
              option={option}
              isChosen={isOptionChosen(item, filter, option.value)}
              onPick={() =>
                setOption({ value: option.value, text: option.text })
              }
            />
          ))}
        </div>
      ) : (
        <p className="sk:m-0 sk:text-dense sk:text-fg-tertiary">
          No Options found
        </p>
      )}
    </div>
  );
}

function OptionRow({
  option,
  isChosen,
  onPick,
}: {
  option: FilterOption;
  isChosen: boolean;
  onPick: () => void;
}) {
  const country = option.flag || option.item?.region;
  const flag = country ? flagImages[country.toUpperCase()] : undefined;

  return (
    <button
      type="button"
      aria-pressed={isChosen}
      onClick={onPick}
      className={cn(
        'sk:flex sk:w-full sk:cursor-pointer sk:items-center sk:justify-between sk:gap-2 sk:rounded-option sk:border-0 sk:bg-transparent sk:px-2 sk:py-2 sk:text-left sk:text-dense sk:text-fg sk:hover:bg-fill-quaternary',
        isChosen && 'sk:bg-fill-quaternary sk:font-semibold',
      )}
    >
      {isValidElement(option.content) ? (
        cloneElement(option.content)
      ) : (
        <span className="sk:flex sk:min-w-0 sk:items-center sk:gap-2">
          {flag ? (
            <img
              src={flag}
              alt=""
              className="sk:h-[15px] sk:w-5 sk:object-cover"
            />
          ) : null}
          {option.image?.src ? (
            <img
              src={option.image.src}
              alt=""
              className="sk:h-[25px] sk:object-contain"
            />
          ) : null}
          <span className="sk:truncate">{option.text}</span>
        </span>
      )}
      <Check size={18} aria-hidden className={isChosen ? '' : 'sk:invisible'} />
    </button>
  );
}

type FilterPillOption = { value: string; conditionImage?: ReactNode };

type FilterPillProps = {
  /** The filter's config, as passed to FilterButton. */
  data: {
    name: string;
    label?: ReactNode;
    icon?: ReactNode;
    custom_options?: FilterPillOption[];
  };
  /** FilterButton's applied values, keyed by filter name. */
  userValue: Record<
    string,
    | { toShow?: ReactNode | ReactNode[]; index?: number; type?: string }
    | undefined
  >;
  removeFilter: (name: string) => void;
  openRelevantFilter: (index: number | undefined) => void;
};

/** An applied filter above a list: opens that filter on click, with a remove button. */
export function FilterPill({
  data,
  userValue,
  removeFilter,
  openRelevantFilter,
}: FilterPillProps) {
  const applied = userValue[data.name];
  const toShow = applied?.toShow;
  const text = useMemo(() => {
    if (Array.isArray(toShow)) {
      /* `slice`, not `splice`: this runs on render and must not edit the filter's state. */
      return toShow.length > 2
        ? `${toShow.slice(0, 2).join(',')}...`
        : toShow.join(',');
    }
    return toShow;
  }, [toShow]);

  const condition = useMemo(() => {
    const options: FilterPillOption[] = data.custom_options ?? filterOptions;
    return options.find(option => option.value === applied?.type)
      ?.conditionImage;
  }, [data, applied?.type]);

  if (!text) {
    return null;
  }

  return (
    <span className="sk:ml-2! sk:inline-flex sk:max-w-full sk:items-center sk:rounded-full sk:bg-surface sk:text-[10px] sk:inset-shadow-hairline sk:hover:bg-fill-quaternary">
      <button
        type="button"
        onClick={() => openRelevantFilter(applied?.index)}
        className="sk:inline-flex sk:min-w-0 sk:cursor-pointer sk:items-center sk:gap-1 sk:border-0 sk:bg-transparent sk:py-[3px] sk:pr-0.5 sk:pl-2 sk:text-fg"
      >
        {isValidElement(data.icon) &&
          cloneElement(data.icon as ReactElement<{ size?: number }>, {
            size: 12,
          })}
        <span className="sk:text-fg-tertiary">{data.label}</span>
        {isValidElement(condition) ? cloneElement(condition) : null}
        <span className="sk:max-w-75 sk:truncate">
          {isValidElement(text) ? cloneElement(text) : text}
        </span>
      </button>
      <button
        type="button"
        aria-label={`Remove ${typeof data.label === 'string' ? data.label : 'filter'}`}
        onClick={() => removeFilter(data.name)}
        className="sk:mr-1 sk:inline-flex sk:size-3.75 sk:shrink-0 sk:cursor-pointer sk:items-center sk:justify-center sk:rounded-full sk:border-0 sk:bg-transparent sk:p-0 sk:text-fg sk:hover:bg-primary sk:hover:text-surface"
      >
        <X size={12} aria-hidden />
      </button>
    </span>
  );
}
